import { mapState } from 'vuex';
import { debounce, get } from 'lodash';
import { getCategoryOptions } from './Utils/labels';
import DynamicInputContainer from './DynamicInputContainer.vue';
import OptionsLoaderMixin from './Mixins/OptionsLoaderMixin';
import PaginationMixin from './Mixins/PaginationMixin';
export default {
  name: 'SearchRuleForm',
  components: {
    DynamicInputContainer
  },
  mixins: [OptionsLoaderMixin, PaginationMixin],
  props: {
    searchConfig: {
      type: Object,
      required: true,
      default: () => ({})
    },
    showCondition: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedField: null,
      selectedCondition: 'is',
      inputValues: {
        value: '',
        key: ''
      },
      keySearchTerm: '',
      valueSearchTerm: '',
      keyPaginationState: {
        page: 1,
        hasMore: true,
        searchTerm: '',
        isLoadingMore: false
      }
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    conditionOptions() {
      return [{
        value: 'is',
        text: this.$i18n_t('Core.MultiSearch.conditions.is')
      }, {
        value: 'isnot',
        text: this.$i18n_t('Core.MultiSearch.conditions.isNot')
      }];
    },
    fieldOptions() {
      try {
        return getCategoryOptions(this.searchConfig, this.$i18n);
      } catch (error) {
        console.error('Error getting field options:', error);
        const categories = get(this.searchConfig, 'categories', {});
        return Object.keys(categories).map(category => ({
          value: category,
          text: categories[category].label || category
        }));
      }
    },
    currentFieldConfig() {
      if (!this.selectedField) {
        return null;
      }
      return get(this.searchConfig, ['categories', this.selectedField], {});
    },
    canAddRule() {
      if (!this.selectedField) {
        return false;
      }
      const config = this.currentFieldConfig;
      if (!config) {
        return false;
      }
      if (config.type === 'keyValue') {
        return this.hasTextValue(this.inputValues.key) && this.hasTextValue(this.inputValues.value);
      }
      return this.hasTextValue(this.inputValues.value);
    },
    canLoadMoreKeyOptions() {
      return this.keyPaginationState.hasMore && !this.isLoadingKeyOptions;
    },
    isLoadingMoreKeyOptions() {
      return this.keyPaginationState.isLoadingMore;
    }
  },
  watch: {
    selectedField: {
      handler(newField) {
        if (newField) {
          this.clearInputs();
          this.loadSelectOptions();
          this.loadKeyValueOptions();
        }
      },
      immediate: true
    },
    searchConfig: {
      handler() {
        if (this.selectedField) {
          this.loadSelectOptions();
          this.loadKeyValueOptions();
        }
      },
      deep: true
    }
  },
  mounted() {
    this.selectFirstField();
  },
  methods: {
    generateRuleId() {
      return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },
    clearInputs() {
      this.inputValues = {
        value: '',
        key: ''
      };
      this.selectOptions = [];
      this.keyOptions = [];
      this.valueOptions = [];
      this.isLoadingOptions = false;
      this.isLoadingKeyOptions = false;
      this.isLoadingValueOptions = false;
      this.lastError = null;
      this.searchTerm = '';
      this.keySearchTerm = '';
      this.valueSearchTerm = '';
      this.resetKeyPaginationState();
      if (this.selectedField) {
        this.resetPaginationState(this.selectedField);
      }
    },
    onKeyChange() {
      const config = this.currentFieldConfig;
      if (!config || !config.fetchValueOptions) {
        return;
      }

      // Selecting a key updates the autocomplete search text internally.
      // Cancel pending key-search debounce to prevent clearing the chosen key.
      if (typeof this.onKeySearchInput.cancel === 'function') {
        this.onKeySearchInput.cancel();
      }
      const hasSelectedKey = this.hasTextValue(this.inputValues.key);
      this.valueSearchTerm = '';
      this.inputValues.value = '';
      this.valueOptions = [];
      if (!hasSelectedKey) {
        return;
      }
      if (this.keySearchTerm !== '') {
        this.keySearchTerm = '';
        this.updateKeyPaginationState({
          page: 1,
          searchTerm: '',
          hasMore: true,
          isLoadingMore: false
        });
        this.loadKeyOptions();
      }
      this.loadValueOptions();
    },
    getKeyPaginationState() {
      return this.keyPaginationState;
    },
    resetKeyPaginationState() {
      this.keyPaginationState = {
        page: 1,
        hasMore: true,
        searchTerm: '',
        isLoadingMore: false
      };
    },
    updateKeyPaginationState(updates) {
      this.keyPaginationState = {
        ...this.keyPaginationState,
        ...updates
      };
    },
    onKeySearchInput: debounce(function (searchTerm) {
      const normalizedSearchTerm = typeof searchTerm === 'string' ? searchTerm.trim() : '';
      if (this.keySearchTerm === normalizedSearchTerm) {
        return;
      }
      const normalizedSelectedKey = this.hasTextValue(this.inputValues.key) ? this.inputValues.key.toString().trim() : '';
      if (normalizedSelectedKey && normalizedSearchTerm === normalizedSelectedKey) {
        return;
      }
      this.keySearchTerm = normalizedSearchTerm;
      const config = this.currentFieldConfig;
      if (!config || config.type !== 'keyValue' || !config.fetchKeyOptions) {
        return;
      }
      this.keyOptions = [];
      this.updateKeyPaginationState({
        page: 1,
        searchTerm: normalizedSearchTerm,
        hasMore: true,
        isLoadingMore: false
      });
      this.loadKeyOptions();
    }, 400),
    onKeyIntersect(entries) {
      if (entries[0].isIntersecting && this.canLoadMoreKeyOptions && !this.isLoadingMoreKeyOptions) {
        this.loadNextKeyPage();
      }
    },
    async loadNextKeyPage() {
      if (!this.canLoadMoreKeyOptions) {
        return;
      }
      this.updateKeyPaginationState({
        page: this.keyPaginationState.page + 1,
        isLoadingMore: true
      });
      await this.loadKeyOptions();
    },
    hasTextValue(value) {
      if (value === null || value === undefined) {
        return false;
      }
      return value.toString().trim() !== '';
    },
    onValueSearchInput: debounce(function (searchTerm) {
      const normalizedSearchTerm = typeof searchTerm === 'string' ? searchTerm.trim() : '';
      if (this.valueSearchTerm === normalizedSearchTerm) {
        return;
      }
      this.valueSearchTerm = normalizedSearchTerm;
      const config = this.currentFieldConfig;
      if (!config || config.type !== 'keyValue' || !config.fetchValueOptions || !this.hasTextValue(this.inputValues.key)) {
        return;
      }
      this.loadValueOptions();
    }, 400),
    clearInputValues() {
      this.inputValues = {
        value: '',
        key: ''
      };
    },
    addRule() {
      if (!this.canAddRule) {
        return;
      }
      const rule = {
        id: this.generateRuleId(),
        field: this.selectedField,
        condition: this.showCondition ? this.selectedCondition : 'is',
        value: this.inputValues.value
      };
      const config = this.currentFieldConfig;
      if (config && config.type === 'keyValue') {
        rule.key = this.inputValues.key;
      }
      const displayText = this.getDisplayTextForValue();
      if (displayText && displayText !== rule.value) {
        rule.text = displayText;
      }
      this.$emit('add-rule', rule);
      this.clearInputValues();
    },
    getDisplayTextForValue() {
      const config = this.currentFieldConfig;
      if (!config) {
        return null;
      }
      if (config.type === 'keyValue') {
        return null;
      }
      if (config.type === 'dtwin' && this.$refs.twinTreeview) {
        return this.$refs.twinTreeview.getSelectedTwinTitle();
      }
      if (config.type === 'select' && config.fetchOptions) {
        const selectedValue = this.inputValues.value;
        const option = this.selectOptions.find(opt => opt.value === selectedValue);
        return option ? option.text : null;
      }
      return null;
    },
    selectFirstField() {
      const categories = get(this.searchConfig, 'categories', {});
      const firstField = Object.keys(categories)[0];
      if (firstField && !this.selectedField) {
        this.selectedField = firstField;
      }
    }
  }
};